import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./ConversionQualityIndicator.css";

interface PixelData {
  pixels: number[][];
  width: number;
  height: number;
}

interface ConversionQualityIndicatorProps {
  pixelData: PixelData | null;
}

type QualityLevel = "good" | "fair" | "poor";

function getQualityLevel(
  uniqueColors: number,
  totalBlocks: number,
  width: number,
  height: number,
): QualityLevel {
  if (totalBlocks < 16) return "poor"; // too coarse
  if (uniqueColors >= 4 && totalBlocks >= 64) return "good";
  if (uniqueColors >= 2 && totalBlocks >= 32) return "fair";
  return "poor";
}

const ConversionQualityIndicator: React.FC<ConversionQualityIndicatorProps> = ({
  pixelData,
}) => {
  const { t } = useTranslation();
  const stats = useMemo(() => {
    if (!pixelData) return null;
    const colorSet = new Set<number>();
    for (let y = 0; y < pixelData.height; y++) {
      for (let x = 0; x < pixelData.width; x++) {
        colorSet.add(pixelData.pixels[y][x]);
      }
    }
    const totalBlocks = pixelData.width * pixelData.height;
    const level = getQualityLevel(
      colorSet.size,
      totalBlocks,
      pixelData.width,
      pixelData.height,
    );
    return {
      uniqueColors: colorSet.size,
      totalBlocks,
      width: pixelData.width,
      height: pixelData.height,
      level,
    };
  }, [pixelData]);

  if (!stats) return null;

  const levelClass = `quality-${stats.level}`;

  return (
    <div className="conversion-quality-indicator" aria-live="polite">
      <h3>{t("quality.title")}</h3>
      <div className={`quality-badge ${levelClass}`}>
        {t(`quality.${stats.level}`)}
      </div>
      <ul className="quality-stats">
        <li>{t("quality.colorsUsed", { count: stats.uniqueColors })}</li>
        <li>
          {t("quality.grid", {
            width: stats.width,
            height: stats.height,
            count: stats.totalBlocks,
          })}
        </li>
      </ul>
      {stats.level === "poor" && (
        <p className="quality-tip">{t("quality.tipPoor")}</p>
      )}
      {stats.level === "fair" && (
        <p className="quality-tip">{t("quality.tipFair")}</p>
      )}
    </div>
  );
};

export default ConversionQualityIndicator;
